import { Controller, useForm } from "react-hook-form";
import { RequestCallbackCommand, useRequestCallbackFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { PageHeader } from "UIPalette/PageHeader/PageHeader";
import Button from "shared/UI/Buttons/Button";
import { UserContext } from "core/state/UserContext/UserContext";
import { ValidatableWrapper } from "shared/UI/Inputs/ValidatableWrapper";
import { phoneNumberRegex } from "shared/utils/regex";
import { t } from "i18next";
import { useContext } from "react";
import PageTitle from "UIPalette/PageTitle/PageTitle";
import { trackFormSubmission } from "core/monitoring/Events";
import LocalizedWrapper from "UIPalette/LocaleWrapper/LocaleWrapper";
import sanitize from "sanitize-html";
import { NarrowContainer } from "shared/UI/LayoutComponents/LayoutComponents";
import { Select } from "shared/UI/Select/Select";
import { Input } from "shared/UI/Input/Input";
import { useNotifyModalContext } from "shared/core/state/NotifyModalContext/NotifyModalContext";

type Inputs = {
  phoneNumber: string;
  reasonForCalling: string;
  leaveVoicemail: string;
  canSms: string;
  callbackTime: string;
};
export const Callback = () => {
  const { modalNotify } = useNotifyModalContext();
  const { trigger: requestCallbackAPI } = useRequestCallbackFunctionRequest();
  const { user } = useContext(UserContext);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, defaultValues },
  } = useForm<Inputs>({
    defaultValues: {
      phoneNumber: user?.phoneNumber ?? "",
      reasonForCalling: "",
      leaveVoicemail: "",
      canSms: "",
      callbackTime: "",
    },
  });

  const handleSanitiseField = (value: string) => sanitize(value, { allowedTags: [], allowedAttributes: {} });

  const onSubmit = async (inputs: Inputs) => {
    const data: RequestCallbackCommand = {
      phoneNumber: handleSanitiseField(inputs.phoneNumber),
      reasonForCalling: handleSanitiseField(inputs.reasonForCalling),
      leaveVoicemail: handleSanitiseField(inputs.leaveVoicemail),
      canSendSms: handleSanitiseField(inputs.canSms),
      callbackTime: handleSanitiseField(inputs.callbackTime),
    };
    trackFormSubmission("Callback", { leaveVoicemail: data.leaveVoicemail, canSendSms: data.canSendSms, callbackTime: data.callbackTime });
    try {
      await requestCallbackAPI({ body: data });
      reset();
      modalNotify("success", t("support.callback.requestSuccessTitle"), t("support.callback.requestSuccessMessage"));
    } catch {
      modalNotify("error", t("support.callback.requestErrorTitle"), t("support.callback.requestErrorMessage"), t("support.callback.requestErrorCta"));
    }
  };

  return (
    <>
      <PageTitle title={t("pageTitle.support.callback")} />
      <PageHeader narrowView title={t("support.callback.title")} />
      <NarrowContainer>
        <p className={"mb-4"}>{t("support.callback.description")}</p>
        <form className="space-y-2" onSubmit={handleSubmit(onSubmit)} id={t("pageTitle.support.callback")}>
          <Input
            helperText={errors.phoneNumber?.message}
            state={errors.phoneNumber ? "error" : undefined}
            type="tel"
            defaultValue={defaultValues?.phoneNumber}
            label={t("support.callback.phoneNumber")}
            placeholder={t("support.callback.phoneNumberPlaceholder")}
            {...register("phoneNumber", {
              required: t("support.callback.phoneNumberErrorMessage"),
              pattern: { value: phoneNumberRegex(), message: t("support.callback.phoneNumberErrorMessage") },
            })}
          />

          <Controller
            name={"reasonForCalling"}
            control={control}
            rules={{
              required: t("support.callback.reasonForCallingErrorMessage"),
            }}
            render={({ field: { onChange, value } }) => (
              <div>
                <Select
                  helperText={errors.reasonForCalling?.message}
                  state={errors.reasonForCalling ? "error" : undefined}
                  aria-label={t("support.callback.reasonForCalling")}
                  id="reasonForCalling"
                  label={t("support.callback.reasonForCalling")}
                  placeholder="Provide a reason for calling"
                  onChange={onChange}
                  value={value}
                >
                  <option key="counselling" value="Counselling">
                    {t("support.callback.reasonForCallingOptions.counselling")}
                  </option>
                  <LocalizedWrapper module="legalInformation">
                    <option key="legalInformation" value="Legal Information">
                      {t("support.callback.reasonForCallingOptions.legalInformation")}
                    </option>
                  </LocalizedWrapper>
                  <LocalizedWrapper module="financialWellbeing">
                    <option key="financialWellbeing" value="Financial Wellbeing">
                      {t("support.callback.reasonForCallingOptions.financialWellbeing")}
                    </option>
                  </LocalizedWrapper>
                  <option key="clientServices" value="Client Services">
                    {t("support.callback.reasonForCallingOptions.clientServices")}
                  </option>
                </Select>
              </div>
            )}
          />

          <div className="my-4">
            <label>{t("support.callback.leaveVoicemail")}</label>
            <ValidatableWrapper
              validationMessage={t("support.callback.requiredErrorMessage")}
              validationState={errors.leaveVoicemail ? "error" : undefined}
            >
              <div className="flex items-center gap-8">
                <div>
                  <input type="radio" id="leaveVoicemailYes" value="yes" className="mr-1" {...register("leaveVoicemail", { required: true })} />
                  <label htmlFor="leaveVoicemailYes">{t("support.callback.yes")}</label>
                </div>

                <div>
                  <input type="radio" id="leaveVoicemailNo" value="no" className="mr-1" {...register("leaveVoicemail", { required: true })} />
                  <label htmlFor="leaveVoicemailNo">{t("support.callback.no")}</label>
                </div>
              </div>
            </ValidatableWrapper>
          </div>

          <div className="my-4">
            <label>{t("support.callback.canSms")}</label>
            <ValidatableWrapper validationMessage={t("support.callback.requiredErrorMessage")} validationState={errors.canSms ? "error" : undefined}>
              <div className="flex items-center gap-8">
                <div>
                  <input type="radio" id="canSmsYes" value="yes" className="mr-1" {...register("canSms", { required: true })} />
                  <label htmlFor="canSmsYes">{t("support.callback.yes")}</label>
                </div>

                <div>
                  <input type="radio" id="canSmsNo" value="no" className="mr-1" {...register("canSms", { required: true })} />
                  <label htmlFor="canSmsNo">{t("support.callback.no")}</label>
                </div>
              </div>
            </ValidatableWrapper>
          </div>

          <div className="my-4">
            <label>{t("support.callback.callbackTime")}</label>
            <ValidatableWrapper
              validationMessage={t("support.callback.requiredErrorMessage")}
              validationState={errors.callbackTime ? "error" : undefined}
            >
              <div className="flex items-center gap-4 whitespace-nowrap flex-wrap">
                <div>
                  <input
                    type="radio"
                    id="callbackTimeAm"
                    value={t("support.callback.am")}
                    className="mr-1"
                    {...register("callbackTime", { required: true })}
                  />
                  <label htmlFor="callbackTimeAm">{t("support.callback.am")}</label>
                </div>

                <div>
                  <input
                    type="radio"
                    id="callbackTimePm"
                    value={t("support.callback.pm")}
                    className="mr-1"
                    {...register("callbackTime", { required: true })}
                  />
                  <label htmlFor="callbackTimePm">{t("support.callback.pm")}</label>
                </div>
              </div>
            </ValidatableWrapper>
          </div>

          <Button type="submit" text={t("support.callback.requestCallback")} aria-label={t("support.callback.requestCallback")} />
        </form>
      </NarrowContainer>
    </>
  );
};
